(() => {
  'use strict';

  const qs = (selector, scope = document) => scope.querySelector(selector);
  const qsa = (selector, scope = document) => [...scope.querySelectorAll(selector)];
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  let activeDrawer = null;
  let drawerTrigger = null;

  function showToast(message) {
    const toast = qs('.ql-toast');
    if (!toast) return;
    toast.textContent = message;
    toast.hidden = false;
    clearTimeout(showToast.timer);
    showToast.timer = setTimeout(() => { toast.hidden = true; }, 3200);
  }

  function overlay() { return qs('[data-drawer-overlay]'); }

  function closeDrawer({ restoreFocus = true } = {}) {
    if (!activeDrawer) return;
    activeDrawer.classList.remove('is-open');
    activeDrawer.setAttribute('aria-hidden', 'true');
    const trigger = document.querySelector(`[aria-controls="${CSS.escape(activeDrawer.id)}"]`);
    trigger?.setAttribute('aria-expanded', 'false');
    overlay()?.setAttribute('hidden', '');
    document.body.classList.remove('is-locked');
    const previous = drawerTrigger;
    activeDrawer = null;
    drawerTrigger = null;
    if (restoreFocus) previous?.focus();
  }

  function openDrawer(id, trigger) {
    const drawer = document.getElementById(id);
    if (!drawer) return;
    closeDrawer({ restoreFocus: false });
    activeDrawer = drawer;
    drawerTrigger = trigger;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    trigger?.setAttribute('aria-expanded', 'true');
    overlay()?.removeAttribute('hidden');
    document.body.classList.add('is-locked');
    requestAnimationFrame(() => qs(focusableSelector, drawer)?.focus());
  }

  function trapFocus(event) {
    const trapRoot = activeDrawer || qs('.facets-drawer.is-open');
    if (!trapRoot || event.key !== 'Tab') return;
    const focusable = qsa(focusableSelector, trapRoot).filter((node) => node.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  function initDrawers(scope = document) {
    qsa('[data-drawer-open]', scope).forEach((button) => {
      if (button.dataset.drawerBound) return;
      button.dataset.drawerBound = 'true';
      button.addEventListener('click', () => openDrawer(button.dataset.drawerOpen, button));
    });
    qsa('[data-drawer-close]', scope).forEach((button) => {
      if (button.dataset.drawerBound) return;
      button.dataset.drawerBound = 'true';
      button.addEventListener('click', () => closeDrawer());
    });
    const pageOverlay = qs('[data-drawer-overlay]', scope) || overlay();
    if (pageOverlay && !pageOverlay.dataset.drawerBound) {
      pageOverlay.dataset.drawerBound = 'true';
      pageOverlay.addEventListener('click', () => {
        closeDrawer();
        closeFacets();
      });
    }
  }

  function initHeader(scope = document) {
    const header = qs('[data-header]', scope);
    if (!header || header.dataset.headerBound) return;
    header.dataset.headerBound = 'true';
    const section = header.closest('.header-section') || header.parentElement;
    const sticky = header.classList.contains('site-header--sticky');
    let ticking = false;
    const sync = () => {
      const stuck = sticky && window.scrollY > 0 && section.getBoundingClientRect().top <= 0;
      header.classList.toggle('is-stuck', stuck);
      header.classList.toggle('is-scrolled', stuck);
      ticking = false;
    };
    const requestSync = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(sync);
    };
    sync();
    window.addEventListener('scroll', requestSync, { passive: true });
    window.addEventListener('resize', requestSync, { passive: true });
  }

  class Slideshow {
    constructor(root) {
      if (root.dataset.initialized) return;
      root.dataset.initialized = 'true';
      this.root = root;
      this.slides = qsa('[data-slide]', root);
      this.dots = qsa('[data-slide-dot]', root);
      this.index = 0;
      this.paused = false;
      this.autoplay = root.dataset.autoplay === 'true' && !reducedMotion.matches;
      this.speed = Number(root.dataset.speed) || 7000;
      qs('[data-slide-prev]', root)?.addEventListener('click', () => this.show(this.index - 1, true));
      qs('[data-slide-next]', root)?.addEventListener('click', () => this.show(this.index + 1, true));
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.show(Number(dot.dataset.slideDot), true)));
      this.pauseButton = qs('[data-slide-pause]', root);
      this.pauseButton?.addEventListener('click', () => this.togglePause());
      root.addEventListener('mouseenter', () => this.stop());
      root.addEventListener('mouseleave', () => this.start());
      root.addEventListener('focusin', () => this.stop());
      root.addEventListener('focusout', () => this.start());
      this.start();
    }
    show(next, restart = false) {
      if (!this.slides.length) return;
      this.index = (next + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, index) => {
        const active = index === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      this.dots.forEach((dot, index) => dot.setAttribute('aria-current', index === this.index ? 'true' : 'false'));
      if (restart) { this.stop(); this.start(); }
    }
    start() {
      if (!this.autoplay || this.paused || this.timer) return;
      this.timer = setInterval(() => this.show(this.index + 1), this.speed);
    }
    stop() { clearInterval(this.timer); this.timer = null; }
    togglePause() {
      this.paused = !this.paused;
      this.pauseButton?.setAttribute('aria-pressed', String(this.paused));
      this.pauseButton?.setAttribute('aria-label', this.paused ? 'Play slideshow' : 'Pause slideshow');
      if (this.paused) this.stop(); else this.start();
    }
  }

  class AnnouncementBar {
    constructor(root) {
      if (root.dataset.initialized) return;
      root.dataset.initialized = 'true';
      this.items = qsa('[data-announcement-item]', root);
      this.index = 0;
      this.speed = Number(root.dataset.speed) || 6000;
      this.autoplay = root.dataset.autoplay === 'true' && !reducedMotion.matches;
      this.paused = false;
      this.pauseButton = qs('[data-announcement-pause]', root);
      qs('[data-announcement-prev]', root)?.addEventListener('click', () => this.show(this.index - 1, true));
      qs('[data-announcement-next]', root)?.addEventListener('click', () => this.show(this.index + 1, true));
      this.pauseButton?.addEventListener('click', () => this.togglePause());
      root.addEventListener('mouseenter', () => this.stop());
      root.addEventListener('mouseleave', () => this.start());
      root.addEventListener('focusin', () => this.stop());
      root.addEventListener('focusout', () => this.start());
      this.start();
    }
    show(next, restart = false) {
      if (!this.items.length) return;
      this.index = (next + this.items.length) % this.items.length;
      this.items.forEach((item, index) => item.classList.toggle('is-active', index === this.index));
      if (restart) { this.stop(); this.start(); }
    }
    start() { if (this.autoplay && !this.paused && !this.timer) this.timer = setInterval(() => this.show(this.index + 1), this.speed); }
    stop() { clearInterval(this.timer); this.timer = null; }
    togglePause() {
      this.paused = !this.paused;
      this.pauseButton?.setAttribute('aria-pressed', String(this.paused));
      this.pauseButton?.setAttribute('aria-label', this.paused ? 'Play announcements' : 'Pause announcements');
      if (this.paused) this.stop(); else this.start();
    }
  }

  class ContentCarousel {
    constructor(root) {
      if (root.dataset.carouselInitialized) return;
      root.dataset.carouselInitialized = 'true';
      this.root = root;
      this.track = qs('[data-carousel-track]', root);
      if (!this.track) return;
      this.previous = qs('[data-carousel-prev]', root);
      this.next = qs('[data-carousel-next]', root);
      this.toggle = qs('[data-carousel-toggle]', root);
      this.autoplay = root.dataset.carouselAutoplay === 'true' && !reducedMotion.matches;
      this.speed = Number(root.dataset.carouselSpeed) || 5000;
      this.paused = false;
      this.previous?.addEventListener('click', () => this.move(-1, true));
      this.next?.addEventListener('click', () => this.move(1, true));
      this.toggle?.addEventListener('click', () => this.togglePause());
      this.track.addEventListener('scroll', () => requestAnimationFrame(() => this.updateControls()), { passive: true });
      root.addEventListener('mouseenter', () => this.stop());
      root.addEventListener('mouseleave', () => this.start());
      root.addEventListener('focusin', () => this.stop());
      root.addEventListener('focusout', () => this.start());
      this.updateControls();
      this.start();
    }
    amount() {
      const item = this.track.firstElementChild;
      if (!item) return this.track.clientWidth;
      const gap = Number.parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap) || 0;
      return item.getBoundingClientRect().width + gap;
    }
    move(direction, restart = false) {
      const rtlDirection = document.documentElement.dir === 'rtl' ? -1 : 1;
      this.track.scrollBy({ left: direction * rtlDirection * this.amount(), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      if (restart) { this.stop(); this.start(); }
    }
    updateControls() {
      if (document.documentElement.dir === 'rtl') return;
      if (this.previous) this.previous.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
    }
    start() {
      if (!this.autoplay || this.paused || this.timer) return;
      this.timer = setInterval(() => {
        const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
        if (atEnd) this.track.scrollTo({ left: 0, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
        else this.move(1);
      }, this.speed);
    }
    stop() { clearInterval(this.timer); this.timer = null; }
    togglePause() {
      this.paused = !this.paused;
      this.toggle?.setAttribute('aria-pressed', String(this.paused));
      this.toggle?.setAttribute('aria-label', this.paused ? 'Play carousel' : 'Pause carousel');
      if (this.toggle?.firstElementChild) this.toggle.firstElementChild.textContent = this.paused ? '▶' : 'Ⅱ';
      if (this.paused) this.stop(); else this.start();
    }
  }

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = qs('[data-predictive-search-input]', this);
      this.results = qs('[data-predictive-search-results]', this);
      if (!this.input || !this.results || this.bound) return;
      this.bound = true;
      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.search(), 250);
      });
      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });
    }
    async search() {
      const term = this.input.value.trim();
      if (term.length < 2) { this.close(); return; }
      this.abortController?.abort();
      this.abortController = new AbortController();
      const base = window.QueenLibas.routes.predictiveSearch;
      const params = new URLSearchParams({ q: term, section_id: 'predictive-search', 'resources[type]': 'product,collection,article,page', 'resources[limit]': '6', 'resources[options][unavailable_products]': 'last' });
      try {
        const response = await fetch(`${base}?${params}`, { signal: this.abortController.signal });
        if (!response.ok) throw new Error(response.statusText);
        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const content = parsed.querySelector('#predictive-search-results');
        this.results.replaceChildren(...(content ? [...content.childNodes].map((node) => node.cloneNode(true)) : []));
        this.input.setAttribute('aria-expanded', content ? 'true' : 'false');
      } catch (error) {
        if (error.name !== 'AbortError') this.close();
      }
    }
    close() { this.results.replaceChildren(); this.input.setAttribute('aria-expanded', 'false'); }
  }

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.input = qs('input', this);
      this.addEventListener('click', (event) => {
        const button = event.target.closest('button[name]');
        if (!button || !this.input) return;
        const previous = Number(this.input.value || 0);
        const step = Number(this.input.step || 1);
        const min = Number(this.input.min || 0);
        const max = this.input.max ? Number(this.input.max) : Infinity;
        this.input.value = String(Math.min(max, Math.max(min, previous + (button.name === 'plus' ? step : -step))));
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }

  class VariantSelects extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.variants = JSON.parse(qs('[data-product-variants]', this)?.textContent || '[]');
      this.addEventListener('change', () => this.update());
    }
    update() {
      const values = qsa('fieldset', this).map((fieldset) => qs('input:checked', fieldset)?.value);
      const variant = this.variants.find((item) => item.options.every((option, index) => option === values[index]));
      const section = this.closest('[data-product-section]');
      qsa('[data-selected-value]', this).forEach((node, index) => { node.textContent = values[index] || ''; });
      const idInput = qs('[data-variant-id]', section);
      const button = qs('[data-add-to-cart]', section);
      const codButton = qs('[data-cod-checkout]', section);
      const buttonText = qs('[data-add-to-cart-text]', section);
      if (!variant) {
        if (idInput) idInput.disabled = true;
        if (button) button.disabled = true;
        if (codButton) codButton.disabled = true;
        if (buttonText) buttonText.textContent = window.QueenLibas.strings.unavailable;
        return;
      }
      if (idInput) { idInput.value = variant.id; idInput.disabled = !variant.available; }
      if (button) button.disabled = !variant.available;
      if (codButton) codButton.disabled = !variant.available;
      if (buttonText) buttonText.textContent = variant.available ? 'Add to Bag' : window.QueenLibas.strings.soldOut;
      const sku = qs('[data-product-sku]', section);
      if (sku) { sku.textContent = variant.sku || ''; sku.closest('.product-sku').hidden = !variant.sku; }
      const currentPrice = qs('.price__current', qs(`[id^="ProductPrice"]`, section));
      const comparePrice = qs('.price__compare', qs(`[id^="ProductPrice"]`, section));
      if (currentPrice) currentPrice.textContent = formatMoney(variant.price);
      if (comparePrice) { comparePrice.textContent = variant.compare_at_price ? formatMoney(variant.compare_at_price) : ''; comparePrice.hidden = !variant.compare_at_price; }
      if (variant.featured_media) activateMedia(section, variant.featured_media.id);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
    }
  }

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.loaded) return;
      this.loaded = true;
      const observer = new IntersectionObserver(async (entries) => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        try {
          const response = await fetch(this.dataset.url);
          const text = await response.text();
          const parsed = new DOMParser().parseFromString(text, 'text/html');
          const section = parsed.querySelector('product-recommendations');
          if (section && (section.childElementCount || section.textContent.trim())) this.replaceChildren(...[...section.childNodes].map((node) => node.cloneNode(true)));
          initProductForms(this);
        } catch (_) { /* Recommendations are optional. */ }
      }, { rootMargin: '0px 0px 300px' });
      observer.observe(this);
    }
  }

  function formatMoney(cents, currency = window.Shopify?.currency?.active || 'PKR') {
    try { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(cents / 100); }
    catch (_) { return `${currency} ${(cents / 100).toFixed(2)}`; }
  }

  function activateMedia(section, mediaId) {
    qsa('[data-media-id]', section).forEach((media) => {
      const active = String(media.dataset.mediaId) === String(mediaId);
      media.classList.toggle('is-active', active);
      media.hidden = !active;
    });
    qsa('[data-media-target]', section).forEach((thumb) => {
      const active = String(thumb.dataset.mediaTarget) === String(mediaId);
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-pressed', String(active));
    });
  }

  function initGallery(scope = document) {
    qsa('[data-media-target]', scope).forEach((button) => {
      if (button.dataset.mediaBound) return;
      button.dataset.mediaBound = 'true';
      button.addEventListener('click', () => activateMedia(button.closest('[data-product-section]'), button.dataset.mediaTarget));
    });
  }

  async function addProduct(form, submitter = null) {
    const errorNode = qs('[data-product-error]', form.closest('.product-buy'));
    const submit = submitter || qs('[type="submit"]', form);
    const checkoutUrl = submit?.hasAttribute('data-cod-checkout') ? submit.dataset.checkoutUrl : '';
    submit?.setAttribute('aria-busy', 'true');
    if (errorNode) errorNode.hidden = true;
    try {
      const response = await fetch(`${window.QueenLibas.routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || window.QueenLibas.strings.cartError);
      if (checkoutUrl) {
        window.location.assign(checkoutUrl);
        return;
      }
      await refreshCart();
      showToast(`${data.product_title || data.title} — ${window.QueenLibas.strings.addedToCart}`);
      const drawer = qs('[data-cart-drawer]');
      if (window.QueenLibas.settings.cartDrawer && drawer) openDrawer(drawer.id, document.querySelector(`[aria-controls="${CSS.escape(drawer.id)}"]`));
      else window.location.assign(window.QueenLibas.routes.cart);
    } catch (error) {
      if (errorNode) { errorNode.textContent = error.message; errorNode.hidden = false; }
      else showToast(error.message);
    } finally { submit?.removeAttribute('aria-busy'); }
  }

  function initProductForms(scope = document) {
    qsa('[data-product-form]', scope).forEach((form) => {
      if (form.dataset.formBound) return;
      form.dataset.formBound = 'true';
      form.addEventListener('submit', (event) => { event.preventDefault(); addProduct(form, event.submitter); });
    });
  }

  function createNode(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function renderCart(cart) {
    qsa('[data-cart-count]').forEach((count) => { count.textContent = cart.item_count; count.hidden = cart.item_count === 0; });
    const body = qs('[data-cart-drawer-body]');
    const footer = qs('[data-cart-footer]');
    const total = qs('[data-cart-total]');
    const heading = qs('[data-cart-heading]');
    if (heading) {
      const key = cart.item_count === 1 ? 'cartItemOne' : 'cartItemOther';
      heading.textContent = window.QueenLibas.strings[key].replace('__count__', cart.item_count);
    }
    if (total) total.textContent = formatMoney(cart.total_price, cart.currency);
    if (footer) footer.hidden = cart.item_count === 0;
    if (!body) return;
    body.replaceChildren();
    if (!cart.items.length) {
      const empty = createNode('div', 'cart-empty');
      const title = createNode('h2', '', window.QueenLibas.strings.emptyCart);
      const text = createNode('p', '', window.QueenLibas.strings.emptyCartMessage);
      const link = createNode('a', 'button button--dark', window.QueenLibas.strings.continueShopping);
      link.href = `${window.QueenLibas.routes.root}collections/all`;
      empty.append(title, text, link);
      body.append(empty);
      return;
    }
    const wrap = createNode('div', 'cart-drawer__items');
    cart.items.forEach((item, index) => {
      const article = createNode('article', 'cart-drawer-item');
      const mediaLink = createNode('a'); mediaLink.href = item.url;
      if (item.featured_image?.url) { const image = createNode('img'); image.src = item.featured_image.url; image.alt = item.featured_image.alt || item.product_title; image.width = 184; image.height = 244; mediaLink.append(image); }
      const info = createNode('div');
      const titleLink = createNode('a'); titleLink.href = item.url; titleLink.append(createNode('strong', '', item.product_title)); info.append(titleLink);
      if (item.variant_title) info.append(createNode('small', '', item.variant_title));
      info.append(createNode('span', '', formatMoney(item.final_line_price, cart.currency)));
      const remove = createNode('button', 'cart-drawer-item__remove'); remove.type = 'button'; remove.dataset.cartRemove = String(index + 1); remove.setAttribute('aria-label', window.QueenLibas.strings.removeItem.replace('__title__', item.product_title)); remove.textContent = '×';
      article.append(mediaLink, info, remove); wrap.append(article);
    });
    body.append(wrap);
  }

  async function refreshCart() {
    const response = await fetch(`${window.QueenLibas.routes.cart}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(window.QueenLibas.strings.cartError);
    const cart = await response.json();
    renderCart(cart);
    return cart;
  }

  async function changeCartLine(line, quantity) {
    const response = await fetch(`${window.QueenLibas.routes.cartChange}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ line, quantity }) });
    const cart = await response.json();
    if (!response.ok) throw new Error(cart.description || window.QueenLibas.strings.cartError);
    renderCart(cart);
  }

  function initCart(scope = document) {
    const drawer = qs('[data-cart-drawer]', scope) || qs('[data-cart-drawer]');
    if (drawer && !drawer.dataset.cartBound) {
      drawer.dataset.cartBound = 'true';
      drawer.addEventListener('click', async (event) => {
        const remove = event.target.closest('[data-cart-remove]');
        if (!remove) return;
        remove.disabled = true;
        try { await changeCartLine(Number(remove.dataset.cartRemove), 0); }
        catch (error) { showToast(error.message); remove.disabled = false; }
      });
    }
  }

  let facetsTrigger = null;
  function openFacets(button) {
    const drawer = qs('[data-facets-drawer]');
    if (!drawer) return;
    facetsTrigger = button;
    drawer.classList.add('is-open'); drawer.setAttribute('aria-hidden', 'false'); button.setAttribute('aria-expanded', 'true');
    overlay()?.removeAttribute('hidden'); document.body.classList.add('is-locked'); qs(focusableSelector, drawer)?.focus();
  }
  function closeFacets() {
    const drawer = qs('[data-facets-drawer]');
    if (!drawer?.classList.contains('is-open')) return;
    drawer.classList.remove('is-open'); drawer.setAttribute('aria-hidden', 'true'); facetsTrigger?.setAttribute('aria-expanded', 'false'); overlay()?.setAttribute('hidden', ''); document.body.classList.remove('is-locked'); facetsTrigger?.focus(); facetsTrigger = null;
  }
  function initFacets(scope = document) {
    qsa('[data-facets-open]', scope).forEach((button) => { if (!button.dataset.bound) { button.dataset.bound = 'true'; button.addEventListener('click', () => openFacets(button)); } });
    qsa('[data-facets-close]', scope).forEach((button) => { if (!button.dataset.bound) { button.dataset.bound = 'true'; button.addEventListener('click', closeFacets); } });
    qsa('[data-sort-select]', scope).forEach((select) => { if (!select.dataset.bound) { select.dataset.bound = 'true'; select.addEventListener('change', () => select.form.submit()); } });
  }

  function initLocalization(scope = document) {
    qsa('[data-localization-select]', scope).forEach((select) => { if (!select.dataset.bound) { select.dataset.bound = 'true'; select.addEventListener('change', () => select.form.submit()); } });
  }

  function initFooter(scope = document) {
    const details = qsa('.footer-menus details', scope);
    if (!details.length) return;
    const media = window.matchMedia('(max-width: 820px)');
    const sync = () => details.forEach((item) => { item.open = !media.matches; });
    sync(); media.addEventListener('change', sync);
  }

  function initMarquees(scope = document) {
    qsa('[data-hotline-marquee]', scope).forEach((marquee) => {
      if (marquee.dataset.initialized) return;
      marquee.dataset.initialized = 'true';
      const toggle = qs('[data-marquee-toggle]', marquee);
      toggle?.addEventListener('click', () => {
        const paused = marquee.classList.toggle('is-paused');
        toggle.setAttribute('aria-pressed', String(paused));
        toggle.setAttribute('aria-label', paused ? 'Play hotline banner' : 'Pause hotline banner');
        toggle.firstElementChild.textContent = paused ? '▶' : 'Ⅱ';
      });
    });
  }

  function initPortraitVideos(scope = document) {
    qsa('[data-portrait-videos]', scope).forEach((section) => {
      if (section.dataset.videoInitialized || section.dataset.playOnView !== 'true' || reducedMotion.matches) return;
      section.dataset.videoInitialized = 'true';
      const videos = qsa('video', section);
      if (!videos.length) return;
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio >= .6) video.play().catch(() => {});
          else video.pause();
        });
      }, { threshold: [.2,.6] });
      videos.forEach((video) => observer.observe(video));
    });
  }

  function initReviewDialogs(scope = document) {
    qsa('.testimonials', scope).forEach((section) => {
      if (section.dataset.reviewDialogInitialized) return;
      section.dataset.reviewDialogInitialized = 'true';
      const dialog = qs('[data-review-dialog]', section);
      if (!dialog) return;
      let lastTrigger = null;
      const titleTarget = qs('[data-review-dialog-title]', dialog);
      const quoteTarget = qs('[data-review-dialog-quote]', dialog);
      const authorTarget = qs('[data-review-dialog-author]', dialog);
      const sourceTarget = qs('[data-review-dialog-source]', dialog);
      const starsTarget = qs('[data-review-dialog-stars]', dialog);
      const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const card = entry.target.closest('[data-review-card]');
          const trigger = qs('[data-review-open]', card);
          if (trigger) trigger.hidden = entry.target.scrollHeight <= entry.target.clientHeight + 2;
        });
      }) : null;

      qsa('[data-review-card]', section).forEach((card) => {
        const quote = qs('[data-review-quote]', card);
        const trigger = qs('[data-review-open]', card);
        if (!quote || !trigger) return;
        const syncTrigger = () => { trigger.hidden = quote.scrollHeight <= quote.clientHeight + 2; };
        resizeObserver?.observe(quote);
        requestAnimationFrame(syncTrigger);
        document.fonts?.ready.then(syncTrigger);
        trigger.addEventListener('click', () => {
          lastTrigger = trigger;
          titleTarget.textContent = qs('h3', card)?.textContent.trim() || 'Customer review';
          quoteTarget.innerHTML = quote.innerHTML;
          authorTarget.textContent = qs('figcaption strong', card)?.textContent.trim() || '';
          sourceTarget.textContent = qs('figcaption span', card)?.textContent.trim() || '';
          starsTarget.innerHTML = qs('.testimonial-card__stars', card)?.innerHTML || '';
          starsTarget.setAttribute('aria-label', qs('.testimonial-card__stars', card)?.getAttribute('aria-label') || 'Review rating');
          section.dispatchEvent(new Event('mouseenter'));
          document.body.classList.add('is-locked');
          dialog.showModal();
        });
      });

      qs('[data-review-close]', dialog)?.addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
      dialog.addEventListener('close', () => {
        document.body.classList.remove('is-locked');
        section.dispatchEvent(new Event('mouseleave'));
        lastTrigger?.focus();
      });
    });
  }

  function initReveals(scope = document) {
    const items = qsa('.section-heading, .collection-card, .product-card, .testimonial-card, .portrait-video-card, .story-tile, .article-card, .image-with-text__media, .image-with-text__content', scope);
    if (!items.length) return;
    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries, currentObserver) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        currentObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -6% 0px', threshold: .08 });
    items.forEach((item, index) => {
      if (item.dataset.revealBound) return;
      item.dataset.revealBound = 'true';
      item.dataset.reveal = '';
      item.style.setProperty('--reveal-delay', `${Math.min(index % 4, 3) * 65}ms`);
      observer.observe(item);
    });
  }

  function init(scope = document) {
    initHeader(scope);
    initDrawers(scope);
    initGallery(scope);
    initProductForms(scope);
    initCart(scope);
    initFacets(scope);
    initLocalization(scope);
    initFooter(scope);
    initMarquees(scope);
    initPortraitVideos(scope);
    initReviewDialogs(scope);
    initReveals(scope);
    qsa('[data-slideshow]', scope).forEach((root) => new Slideshow(root));
    qsa('[data-announcement-bar]', scope).forEach((root) => new AnnouncementBar(root));
    qsa('[data-content-carousel]', scope).forEach((root) => new ContentCarousel(root));
  }

  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);
  if (!customElements.get('variant-selects')) customElements.define('variant-selects', VariantSelects);
  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') { closeDrawer(); closeFacets(); }
    trapFocus(event);
  });
  document.addEventListener('visibilitychange', () => {
    qsa('[data-slideshow]').forEach((root) => root.dispatchEvent(new Event(document.hidden ? 'mouseenter' : 'mouseleave')));
  });
})();
